import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Heading from "../../elements/heading";
import PrimaryButton from "../../elements/primary-button";
import SimpleLink from "../../elements/simple-link";
import Input from "../../elements/input";
import Modal from "../../components/Modal";
import BodyText from "../../elements/body-text";
import "./style.scss";
import VerifyPhoneNumberForm from "../../sections/verify-phone-number-form";
import api from "../../utils/api";
import { getEncryptor } from "../../services/siteService";
import { codeTypes } from "../../utils/lynkco-constants";
import { getUserInfo } from "../../services/userService";
import { useDispatch, useSelector } from "react-redux";
import { setUserInfoAction } from "../../redux/actions";
import { getSMSCode, updateMobile } from "../../services/verificationService";
import { selectUserState } from "../../selectors/application";
import { S_LANG, webStorage } from "../../utils/webStorage";

function ChangePhoneNumberModal({ phoneAreaEntered, phoneNumberEntered, open, onCloseClicked, onUpdateSuccess, onForgetPassword, fields }) {
  /*Redux start*/
  const dispatch = useDispatch();
  const user = useSelector(selectUserState);
  const setUserInfo = payload => dispatch(setUserInfoAction(payload));
  /*Redux end*/
  const lang = webStorage.get(S_LANG);
  const {
    headingTypePassword,
    headingBodyPassword,
    subHeadingTypePassword,
    subHeadingBodyPassword,
    bodyPassword,
    disclaimerPassword,
    passwordLabel,
    passwordWarningMessage,
    passwordMissingMessage,
    ctaNamePassword,
    ctaTypePassword,
    colorSchemePassword,
    iconNamePassword,
    ctaNameForgot,
    passwordFailedMessage,
    headingTypeCode,
    headingBodyCode,
    verifyPhoneNumberForm,
    resetPassword
  } = fields;
  const [passwordEntered, setPasswordEntered] = useState();
  const [passwordInvalid, setPasswordInvalid] = useState(false);
  const [passwordIsVerified, setPasswordIsVerified] = useState(false);
  const [mobileUpdateFailed, setMobileUpdateFailed] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [sendCodeFailed, setSendCodeFailed] = useState(false);
  const [verifyCodeFailed, setVerifyCodeFailed] = useState(false);
  const [mobileInUse, setMobileInUse] = useState(false);
  useEffect(() => {
    if (open) {
      setPasswordInvalid(false);
      setPasswordIsVerified(false);
      setMobileUpdateFailed(false);
      setPasswordError(null);
      setSendCodeFailed(false);
      setMobileInUse(false);
      setVerifyCodeFailed(false);
    }
  }, [open]);
  const onPasswordBlur = (evt, isValid) => {
    setPasswordEntered(evt.target.value);
    setPasswordInvalid(!isValid);
    if (!isValid) {
      setPasswordError(passwordWarningMessage.value);
    } else {
      setPasswordError(null);
    }
  };
  const onPasswordSubmit = async evt => {
    setSendCodeFailed(false);
    if (!passwordEntered) {
      setPasswordError(passwordMissingMessage.value);
      return;
    }
    if (passwordInvalid) {
      setPasswordError(passwordWarningMessage.value);
      return;
    }
    const encryptor = await getEncryptor(user.email);
    const verifyRes = await api.password
      .verify(null, {
        email: user.email,
        password: encryptor.encrypt(passwordEntered)
      })
      .catch(() => {
        setPasswordError(passwordFailedMessage.value);
      });
    if (verifyRes) {
      setPasswordIsVerified(true);
      await getSMSCode(phoneAreaEntered, phoneNumberEntered, codeTypes.updateMobile);
    }
  };
  const modalProps = {
    showModal: open,
    onCloseClicked: evt => {
      onCloseClicked && onCloseClicked(evt);
    }
  };
  const forgetPasswordProps = {
    text: ctaNameForgot?.value,
    callback: evt => onForgetPassword(evt)
  };

  const verifyPhoneNumberProps = {
    ...verifyPhoneNumberForm?.fields,
    phoneAreaEntered,
    phoneNumberEntered,
    codeType: codeTypes.updateMobile,
    lang,
    sendCodeFailed,
    verifyCodeFailed,
    submitFailed: mobileUpdateFailed,
    mobileInUse,
    onVerificationCodeSubmitted: async (evt, data) => {
      setMobileUpdateFailed(false);
      setVerifyCodeFailed(false);
      try {
        await updateMobile(phoneAreaEntered, phoneNumberEntered, data.code, user.userAccount);
        const userInfo = await getUserInfo();
        setUserInfo(userInfo);
        onUpdateSuccess && onUpdateSuccess(evt, phoneAreaEntered, phoneNumberEntered);
      } catch (ex) {
        console.log("ex", ex);
        switch (ex.code) {
          case "USER_CUSTOMER_026": {
            setMobileInUse(true);
            break;
          }
          case "USER_CUSTOMER_0011": {
            setVerifyCodeFailed(true);
            break;
          }
          default: {
            setMobileUpdateFailed(true);
            break;
          }
        }
      }
    }
  };

  return (
    <Modal className="change-phone-number-modal" {...modalProps}>
      {/* Enter password step */}
      {!passwordIsVerified && (
        <React.Fragment>
          <Heading type={headingTypePassword} html={headingBodyPassword} className="change-phone-number-modal-heading" typographyClass="type-h1" />

          <Heading type={subHeadingTypePassword} html={subHeadingBodyPassword} typographyClass="type-h3" className="change-phone-number-modal-sub-heading" />

          <BodyText html={bodyPassword?.value} className="change-phone-number-modal-body" />

          <Input
            {...{
              label: passwordLabel?.value,
              showError: !!passwordError,
              warningMessage: passwordError,
              value: passwordEntered,
              onBlur: (evt, isValid) => onPasswordBlur(evt, isValid)
            }}
            type="password"
            className="change-phone-number-modal-input"
          />

          <div className="change-phone-number-modal-button">
            <PrimaryButton
              {...{
                ctaType: ctaTypePassword,
                buttonText: ctaNamePassword?.value,
                colorScheme: colorSchemePassword,
                iconName: iconNamePassword,
                callback: evt => onPasswordSubmit(evt)
              }}
            />
            {ctaNameForgot.value && resetPassword && <SimpleLink {...forgetPasswordProps} />}
          </div>

          {disclaimerPassword?.value && (
            <div className="change-phone-number-modal-disclaimer">
              <BodyText html={disclaimerPassword.value} className="disclaimer" />
            </div>
          )}
        </React.Fragment>
      )}
      {/* Enter verification code step */}
      {passwordIsVerified && (
        <React.Fragment>
          <Heading type={headingTypeCode} html={headingBodyCode} className="change-phone-number-modal-heading" typographyClass="type-h1" />
          <VerifyPhoneNumberForm {...verifyPhoneNumberProps} />
        </React.Fragment>
      )}
    </Modal>
  );
}
export default ChangePhoneNumberModal;
ChangePhoneNumberModal.propTypes = {
  userIsVerified: PropTypes.bool,
  enterPasswordStep: PropTypes.shape({
    heading: PropTypes.shape({
      type: PropTypes.string,
      className: PropTypes.string,
      /** This takes the component is RTF */
      html: PropTypes.string
    }),
    subHeading: PropTypes.shape({
      type: PropTypes.string,
      className: PropTypes.string,
      html: PropTypes.string
    }),
    body: PropTypes.string,
    button: PropTypes.shape({
      callback: PropTypes.func,
      icon: PropTypes.shape({ svg: PropTypes.string }),
      colorScheme: PropTypes.string,
      width: PropTypes.string,
      isDisabled: PropTypes.bool,
      internalLink: PropTypes.any,
      transition: PropTypes.any,
      buttonText: PropTypes.string
    }),
    input: PropTypes.shape({
      pattern: PropTypes.string,
      infoMessage: PropTypes.string,
      warningMessage: PropTypes.string,
      value: PropTypes.string,
      type: PropTypes.string,
      onBlur: PropTypes.func,
      onChange: PropTypes.func,
      onFocus: PropTypes.func,
      hasError: PropTypes.bool
    }),
    link: PropTypes.shape({
      internalLink: PropTypes.string,
      buttonText: PropTypes.string.isRequired,
      externalLink: PropTypes.string,
      className: PropTypes.string
    }),
    disclaimer: PropTypes.shape({
      body: PropTypes.any
    }),
    serverError: PropTypes.shape({
      hasError: PropTypes.bool,
      message: PropTypes.string
    })
  }),
  enterVerificationCodeStep: PropTypes.object
};
